// src/common/usage-summary.js
/**
 * Usage summary utilities for fetching and displaying operation costs.
 */

/**
 * Fetch usage summary from the API.
 * 
 * @param {string} apiBase - Base URL of the API
 * @param {string} accessToken - Bearer token for authentication
 * @param {string|null} startDate - Start date (YYYY-MM-DD) or null
 * @param {string|null} endDate - End date (YYYY-MM-DD) or null
 * @returns {Promise<Object>} Usage summary response
 */
export async function fetchUsageSummary(apiBase, accessToken, startDate = null, endDate = null) {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const url = `${apiBase}/usage/summary${params.toString() ? '?' + params.toString() : ''}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      console.warn('[USAGE] Failed to fetch usage summary:', response.status);
      return null;
    }
    
    return await response.json();
  } catch (error) {
    console.error('[USAGE] Error fetching usage summary:', error);
    return null;
  }
}

/**
 * Format cents to USD display string.
 * 
 * @param {number} cents - Amount in cents
 * @returns {string} Formatted USD string
 */
function formatCents(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Render usage summary into a container element.
 * 
 * @param {Object} summary - Usage summary from API
 * @param {HTMLElement} container - Container element to render into
 */
export function renderUsageSummary(summary, container) {
  if (!container || !summary) return;
  
  const totalCostUsd = formatCents(summary.total_cost_cents || 0);
  
  let html = `
    <div class="usage-total">
      <strong>Total Cost:</strong> ${totalCostUsd}
    </div>
  `;
  
  const operationTypes = summary.by_operation_type || {};
  
  // LLM calls
  if (operationTypes.llm) {
    const llm = operationTypes.llm;
    const llmCost = formatCents(llm.cost_cents || 0);
    html += `
      <div class="usage-category">
        <h4>LLM Calls (${llm.calls || 0} calls) — ${llmCost}</h4>
        ${renderProviderBreakdown(llm.by_provider)}
      </div>
    `;
  }
  
  // Embeddings
  if (operationTypes.embedding) {
    const emb = operationTypes.embedding;
    const embCost = formatCents(emb.cost_cents || 0);
    html += `
      <div class="usage-category">
        <h4>Embeddings (${emb.calls || 0} calls) — ${embCost}</h4>
        ${renderProviderBreakdown(emb.by_provider)}
      </div>
    `;
  }
  
  // Web search
  if (operationTypes.web_search) {
    const ws = operationTypes.web_search;
    const wsCost = formatCents(ws.cost_cents || 0);
    html += `
      <div class="usage-category">
        <h4>Web Search (${ws.calls || 0} calls) — ${wsCost}</h4>
        ${renderProviderBreakdown(ws.by_provider)}
      </div>
    `;
  }
  
  // Date range info
  if (summary.date_range) {
    const { start, end } = summary.date_range;
    if (start || end) {
      html += `
        <div class="usage-date-info">
          <small>Period: ${start || 'all time'} to ${end || 'now'}</small>
        </div>
      `;
    }
  }
  
  container.innerHTML = html;
}

/**
 * Render provider breakdown as HTML list.
 * 
 * @param {Object} byProvider - Provider breakdown object
 * @returns {string} HTML string
 */
function renderProviderBreakdown(byProvider) {
  if (!byProvider || Object.keys(byProvider).length === 0) {
    return '<ul><li class="empty">No data</li></ul>';
  }
  
  const items = Object.entries(byProvider).map(([provider, stats]) => {
    const cost = formatCents(stats.cost_cents || 0);
    return `<li><strong>${provider}:</strong> ${stats.calls || 0} calls — ${cost}</li>`;
  });
  
  return `<ul>${items.join('')}</ul>`;
}

/**
 * Initialize usage modal functionality.
 * 
 * @param {string} apiBase - Base URL of the API
 * @param {Function} getAccessToken - Function that returns current access token
 */
export function initUsageModal(apiBase, getAccessToken) {
  // Create modal if it doesn't exist
  let modal = document.getElementById('usage-modal');
  if (!modal) {
    modal = createUsageModal();
    document.body.appendChild(modal);
  }
  
  // Wire up close button
  const closeBtn = modal.querySelector('.close-modal');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      modal.style.display = 'none';
    });
  }
  
  // Wire up refresh button
  const refreshBtn = document.getElementById('usage-refresh-btn');
  if (refreshBtn) {
    refreshBtn.addEventListener('click', async () => {
      await refreshUsageSummary(apiBase, getAccessToken);
    });
  }
  
  // Listen for open event
  window.addEventListener('open-usage-modal', async () => {
    modal.style.display = 'flex';
    await refreshUsageSummary(apiBase, getAccessToken);
  });
  
  // Close on background click
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      modal.style.display = 'none';
    }
  });
}

/**
 * Create the usage modal HTML.
 * 
 * @returns {HTMLElement} Modal element
 */
function createUsageModal() {
  const modal = document.createElement('div');
  modal.id = 'usage-modal';
  modal.className = 'modal usage-modal';
  modal.style.display = 'none';
  
  // Get default date range (last 30 days)
  const endDate = new Date().toISOString().split('T')[0];
  const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  
  modal.innerHTML = `
    <div class="modal-content">
      <span class="close-modal">&times;</span>
      <h3>Usage Summary</h3>
      
      <div id="usage-date-range" class="usage-date-range">
        <label>From:</label>
        <input type="date" id="usage-start-date" value="${startDate}">
        <label>To:</label>
        <input type="date" id="usage-end-date" value="${endDate}">
        <button id="usage-refresh-btn" class="btn-secondary btn-small">Refresh</button>
      </div>
      
      <div id="usage-summary" class="usage-summary">
        <p class="loading">Loading usage data...</p>
      </div>
    </div>
  `;
  
  return modal;
}

/**
 * Refresh the usage summary display.
 * 
 * @param {string} apiBase - Base URL of the API
 * @param {Function} getAccessToken - Function that returns current access token
 */
async function refreshUsageSummary(apiBase, getAccessToken) {
  const container = document.getElementById('usage-summary');
  const startInput = document.getElementById('usage-start-date');
  const endInput = document.getElementById('usage-end-date');
  
  if (!container) return;
  
  container.innerHTML = '<p class="loading">Loading usage data...</p>';
  
  const token = await getAccessToken();
  if (!token) {
    container.innerHTML = '<p class="error">Not authenticated</p>';
    return;
  }
  
  const startDate = startInput?.value || null;
  const endDate = endInput?.value || null;
  
  const summary = await fetchUsageSummary(apiBase, token, startDate, endDate);
  
  if (summary) {
    renderUsageSummary(summary, container);
  } else {
    container.innerHTML = '<p class="error">Failed to load usage data</p>';
  }
}

